import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { CLICKED_ON_QUICK_RESPONSE } from '../../constants/biEvents';
import { useMessage } from '../../context/contexts/message';
import { useReporting } from '../../context/contexts/reporting';
import {
    selectSellerOffersHourlyRate,
    selectShowQuickQuestions,
    selectSubCategoryId,
    useStaticContext
} from '../../context/contexts/static';
import { SubCategory } from '../../../types';
import { useDimensionContext } from '../../context/contexts/dimension';
import {
    INITIAL_QUESTIONS,
    INITIAL_QUESTIONS_VOICE_OVER,
    QUESTION_TO_NEXT_QUESTIONS,
    QUICK_QUESTIONS_CAROUSEL_HEIGHT_THRESHOLD,
    QUICK_QUESTIONS_HIDE_LENGTH_THRESHOLD,
    QUICK_QUESTIONS_CAROUSEL_MESSAGE_LENGTH_THRESHOLD,
    INITIAL_QUESTIONS_WEBSITE_DEVELOPMENT,
    INITIAL_QUESTIONS_VOICE_OVER_HOURLY,
    INITIAL_QUESTIONS_WEBSITE_DEVELOPMENT_HOURLY, INITIAL_QUESTIONS_HOURLY
} from './constants';
import QuickQuestionCarousel from './QuickQuestionsCarousel';
import QuickQuestionsList from './QuickQuestionsList';

const getNextSteps = ({ seenSteps, prevSteps, step }) => {
    let nextSteps;

    const nextQuestions = QUESTION_TO_NEXT_QUESTIONS;

    if (!(step in nextQuestions)) {
        nextSteps = prevSteps.filter((stepOption) => step !== stepOption);
    } else {
        nextSteps = nextQuestions[step];
    }

    seenSteps.add(step);

    return nextSteps.filter((currStep) => !seenSteps.has(currStep));
};

const QuickQuestions = ({ containerClassName, hideOnLongText }) => {
    const { appendToMessage, message } = useMessage();
    const emptyMessage = !message || message.length === 0;

    const context = useStaticContext();
    const { height } = useDimensionContext();
    const subCategoryId = selectSubCategoryId(context);
    const showQuickQuestions = selectShowQuickQuestions(context);
    const sellerOffersHourlyRate = selectSellerOffersHourlyRate(context);

    const getInitialQuestions = () => {
        if (subCategoryId === SubCategory.VOICE_OVER) {
            if (sellerOffersHourlyRate) {
                return INITIAL_QUESTIONS_VOICE_OVER_HOURLY;
            }
            return INITIAL_QUESTIONS_VOICE_OVER;
        }
        if (subCategoryId === SubCategory.WEBSITE_DEVELOPMENT) {
            if (sellerOffersHourlyRate) {
                return INITIAL_QUESTIONS_WEBSITE_DEVELOPMENT_HOURLY;
            }
            return INITIAL_QUESTIONS_WEBSITE_DEVELOPMENT;
        }
        if (sellerOffersHourlyRate) {
            return INITIAL_QUESTIONS_HOURLY;
        }
        return INITIAL_QUESTIONS;
    };

    const initialQuestions = getInitialQuestions();

    const [currentSteps, setCurrentSteps] = useState(initialQuestions);
    const [stepsCounter, setStepsCounter] = useState(1);
    const seenStepsRef = useRef(new Set());

    const { sendBiEvent, sendMixpanelEvent } = useReporting();

    const onStepClick = ({ step, message }) => {
        appendToMessage({
            value: message
        });

        setCurrentSteps((prevSteps) => getNextSteps({
            seenSteps: seenStepsRef.current,
            prevSteps,
            step
        }));

        setStepsCounter((count) => count + 1);

        const event = {
            type: CLICKED_ON_QUICK_RESPONSE,
            params: {
                step: stepsCounter,
                message: step,
                metadata: currentSteps
            }
        };

        sendBiEvent(event);
        sendMixpanelEvent(event);
    };

    const resetQuickQuestions = useCallback(() => {
        setCurrentSteps(initialQuestions);
        seenStepsRef.current = new Set();
        setStepsCounter(1);
    }, [initialQuestions]);

    useEffect(() => {
        emptyMessage && resetQuickQuestions();
    }, [emptyMessage, resetQuickQuestions]);

    if (!showQuickQuestions || isEmpty(currentSteps) || hideOnLongText && message.length >= QUICK_QUESTIONS_HIDE_LENGTH_THRESHOLD) {
        return null;
    }

    const showCarouselByElementHeight = height <= QUICK_QUESTIONS_CAROUSEL_HEIGHT_THRESHOLD;
    const showCarouselByMessageLength = message.length > QUICK_QUESTIONS_CAROUSEL_MESSAGE_LENGTH_THRESHOLD;
    const showCarousel = showCarouselByElementHeight || showCarouselByMessageLength;

    const QuickQuestionsComponent = showCarousel ? QuickQuestionCarousel : QuickQuestionsList;

    return (
        <QuickQuestionsComponent className={containerClassName}
            currentSteps={currentSteps}
            onStepClick={onStepClick}/>
    );
};

QuickQuestions.propTypes = {
    containerClassName: PropTypes.string,
    hideOnLongText: PropTypes.bool
};

export default QuickQuestions;
